import React, { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Loader2, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  MEETING_DAYS,
  hoursFromBusiness,
  newMeetingType,
  normalizeHours,
  normalizeTypes,
  screenPath,
  slugifyScreenName,
  type BusinessHoursInput,
  type MeetingScreen,
  type MeetingTypeDraft,
  type WeekHours,
} from '../../lib/meetingScreens'

type SupabaseLike = {
  from: (table: string) => any
}

type Draft = {
  id: string | null
  name: string
  slug: string
  slugTouched: boolean
  enabled: boolean
  headline: string
  intro: string
  host_name: string
  show_logo: boolean
  accent_color: string
  background_color: string
  return_label: string
  return_url: string
  timezone: string
  hours: WeekHours
  meeting_types: MeetingTypeDraft[]
  buffer_minutes: number
  min_notice_hours: number
  window_days: number
  start_interval_minutes: number
  look_busier: boolean
}

const DURATIONS = [15, 30, 45, 60, 90]

export default function MeetingScreensPanel({
  supabase,
  businessId,
  calendarSyncId,
  businessSlug,
  businessName,
  timezone,
  businessHours,
  logoUrl,
}: {
  supabase: SupabaseLike
  businessId: string
  calendarSyncId: string | null
  businessSlug: string
  businessName: string
  timezone: string
  businessHours?: BusinessHoursInput
  logoUrl?: string | null
}) {
  const [screens, setScreens] = useState<MeetingScreen[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('meeting_screens')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: true })
    if (error) {
      console.error(error)
      toast.error('Could not load meeting screens')
      setScreens([])
    } else {
      setScreens((data || []).map(mapScreen))
    }
    setLoading(false)
  }, [businessId, supabase])

  useEffect(() => { void load() }, [load])

  const startNew = () => {
    setDraft({
      id: null,
      name: 'Intro meeting',
      slug: 'intro-meeting',
      slugTouched: false,
      enabled: true,
      headline: `Book a meeting with ${businessName || 'us'}`,
      intro: 'Pick a time that works for you. You will get a Google Meet link and a calendar invite.',
      host_name: businessName || '',
      show_logo: false,
      accent_color: '#2563eb',
      background_color: '#eef2ff',
      return_label: '',
      return_url: '',
      timezone: timezone || 'America/Vancouver',
      hours: hoursFromBusiness(businessHours),
      meeting_types: [newMeetingType()],
      buffer_minutes: 0,
      min_notice_hours: 4,
      window_days: 30,
      start_interval_minutes: 30,
      look_busier: false,
    })
  }

  const startEdit = (screen: MeetingScreen) => {
    setDraft({
      id: screen.id,
      name: screen.name,
      slug: screen.slug,
      slugTouched: true,
      enabled: screen.enabled,
      headline: screen.headline,
      intro: screen.intro,
      host_name: screen.host_name,
      show_logo: screen.show_logo,
      accent_color: screen.accent_color,
      background_color: screen.background_color,
      return_label: screen.return_label,
      return_url: screen.return_url,
      timezone: screen.timezone,
      hours: screen.hours,
      meeting_types: screen.meeting_types.length ? screen.meeting_types : [newMeetingType()],
      buffer_minutes: screen.buffer_minutes,
      min_notice_hours: screen.min_notice_hours,
      window_days: screen.window_days,
      start_interval_minutes: screen.start_interval_minutes,
      look_busier: screen.look_busier,
    })
  }

  const save = async () => {
    if (!draft) return
    const name = draft.name.trim()
    const slug = slugifyScreenName(draft.slug)
    const types = draft.meeting_types
      .map((type) => ({ ...type, name: type.name.trim() }))
      .filter((type) => type.name)
    const openDay = MEETING_DAYS.some((day) => draft.hours[day.key].some((w) => w.end > w.start))
    if (!name) return toast.error('Name the meeting screen')
    if (!slug) return toast.error('Add a link name')
    if (!types.length) return toast.error('Add at least one meeting type')
    if (types.some((type) => type.duration_minutes < 5 || type.duration_minutes > 480)) {
      return toast.error('Meeting length must be between 5 and 480 minutes')
    }
    if (!openDay) return toast.error('Open at least one day')
    if (!isHex(draft.accent_color) || !isHex(draft.background_color)) return toast.error('Use hex colors like #2563eb')
    const returnLabel = draft.return_label.trim().slice(0, 80)
    const returnUrl = httpUrl(draft.return_url)
    if ((returnLabel || draft.return_url.trim()) && !returnUrl) return toast.error('Use a full link for the button, like https://example.com')
    if (returnUrl && !returnLabel) return toast.error('Add the text for the button')
    try {
      Intl.DateTimeFormat('en-US', { timeZone: draft.timezone.trim() || 'America/Vancouver' })
    } catch {
      return toast.error('Use a time zone like America/Vancouver')
    }

    setSaving(true)
    const payload = {
      business_id: businessId,
      calendar_sync_id: calendarSyncId,
      name,
      slug,
      enabled: draft.enabled,
      headline: draft.headline.trim() || 'Book a meeting',
      intro: draft.intro.trim(),
      host_name: draft.host_name.trim(),
      show_logo: draft.show_logo,
      accent_color: draft.accent_color,
      background_color: draft.background_color,
      return_label: returnLabel,
      return_url: returnUrl,
      timezone: draft.timezone.trim() || timezone || 'America/Vancouver',
      hours: draft.hours,
      meeting_types: types,
      buffer_minutes: clamp(draft.buffer_minutes, 0, 240),
      min_notice_hours: clamp(draft.min_notice_hours, 0, 168),
      window_days: clamp(draft.window_days, 1, 90),
      start_interval_minutes: [15, 30, 60].includes(draft.start_interval_minutes) ? draft.start_interval_minutes : 30,
      look_busier: draft.look_busier,
      updated_at: new Date().toISOString(),
    }
    const query = draft.id
      ? supabase.from('meeting_screens').update(payload).eq('id', draft.id).eq('business_id', businessId)
      : supabase.from('meeting_screens').insert(payload)
    const { error } = await query
    setSaving(false)
    if (error) {
      const message = String(error.message || '')
      toast.error(message.includes('meeting_screens_business_id_slug') || message.includes('duplicate')
        ? 'That link is already used by another screen'
        : 'Could not save the meeting screen')
      return
    }
    toast.success(draft.id ? 'Meeting screen saved' : 'Meeting screen created')
    setDraft(null)
    await load()
  }

  const remove = async (screen: MeetingScreen) => {
    if (!window.confirm(`Delete “${screen.name}”? The public link will stop working.`)) return
    const { error } = await supabase.from('meeting_screens').delete().eq('id', screen.id).eq('business_id', businessId)
    if (error) {
      toast.error('Could not delete the meeting screen')
      return
    }
    toast.success('Meeting screen deleted')
    if (draft?.id === screen.id) setDraft(null)
    await load()
  }

  const copyLink = async (slug: string) => {
    const url = `${window.location.origin}${screenPath(businessSlug, slug)}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied')
    } catch {
      toast.error(url)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Meeting screens</h3>
        <p className="text-xs text-gray-500 mt-1">
          A branded page where someone picks an open time on this Google Calendar and gets a Google Meet invite.
        </p>
      </div>

      {draft ? (
        <div className="p-4 sm:p-5 space-y-4">
          <button type="button" onClick={() => setDraft(null)} className="text-xs font-medium text-blue-600 hover:text-blue-700">
            Back to screens
          </button>
          <Field label="Name">
            <input
              value={draft.name}
              onChange={(e) => setDraft({
                ...draft,
                name: e.target.value,
                slug: draft.slugTouched ? draft.slug : slugifyScreenName(e.target.value),
              })}
              className={inputClass}
            />
          </Field>
          <Field label="Public link">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 shrink-0">/{businessSlug}/meet/</span>
              <input
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value, slugTouched: true })}
                className={inputClass}
              />
            </div>
          </Field>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700">Page is live</span>
            <Toggle on={draft.enabled} onClick={() => setDraft({ ...draft, enabled: !draft.enabled })} />
          </label>
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <label className="flex items-center justify-between gap-3">
              <span>
                <span className="block text-sm text-gray-700">Show logo</span>
                <span className="block text-xs text-gray-500">Uses the logo saved in Business Settings.</span>
              </span>
              <Toggle on={draft.show_logo} onClick={() => setDraft({ ...draft, show_logo: !draft.show_logo })} />
            </label>
            {draft.show_logo && logoUrl && (
              <img src={logoUrl} alt="" className="h-10 max-w-[160px] object-contain object-left" />
            )}
            {draft.show_logo && !logoUrl && (
              <p className="text-xs text-amber-700">Add a logo in Business Settings and it will show on this page.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ColorField
                label="Button color"
                value={draft.accent_color}
                onChange={(accent_color) => setDraft({ ...draft, accent_color })}
              />
              <ColorField
                label="Page color"
                value={draft.background_color}
                onChange={(background_color) => setDraft({ ...draft, background_color })}
              />
            </div>
          </div>
          <Field label="Heading">
            <input
              value={draft.headline}
              onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
              placeholder="Book a meeting with NexusAi"
              className={inputClass}
            />
            <span className="block text-xs text-gray-500 mt-1">The large line on the page. Replace the whole sentence, including “Book a meeting with”.</span>
          </Field>
          <Field label="Introduction">
            <textarea
              value={draft.intro}
              rows={3}
              onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
              className={inputClass}
            />
          </Field>
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <div>
              <p className="text-sm text-gray-700">Button after booking</p>
              <p className="text-xs text-gray-500 mt-0.5">Shown on the confirmation page next to Open Google Meet. Leave both blank to hide it.</p>
            </div>
            <Field label="Button text">
              <input
                value={draft.return_label}
                onChange={(e) => setDraft({ ...draft, return_label: e.target.value })}
                placeholder="Back to our website"
                className={inputClass}
              />
            </Field>
            <Field label="Button link">
              <input
                value={draft.return_url}
                onChange={(e) => setDraft({ ...draft, return_url: e.target.value })}
                placeholder="https://example.com"
                className={inputClass}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name on the page">
              <input value={draft.host_name} onChange={(e) => setDraft({ ...draft, host_name: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Time zone">
              <input value={draft.timezone} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} className={inputClass} />
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-600">Meeting types</p>
              <button
                type="button"
                onClick={() => setDraft({ ...draft, meeting_types: [...draft.meeting_types, { ...newMeetingType(), name: 'Meeting' }] })}
                className="text-xs font-medium text-blue-600"
              >
                Add type
              </button>
            </div>
            <div className="space-y-2">
              {draft.meeting_types.map((type, index) => (
                <div key={type.id} className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      value={type.name}
                      onChange={(e) => updateType(setDraft, index, { name: e.target.value })}
                      placeholder="Name"
                      className={inputClass}
                    />
                    <select
                      value={type.duration_minutes}
                      onChange={(e) => updateType(setDraft, index, { duration_minutes: Number(e.target.value) })}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      {DURATIONS.map((minutes) => (
                        <option key={minutes} value={minutes}>{minutes} min</option>
                      ))}
                    </select>
                    {draft.meeting_types.length > 1 && (
                      <button type="button" onClick={() => setDraft((prev) => prev && ({ ...prev, meeting_types: prev.meeting_types.filter((_, i) => i !== index) }))} className="px-2 text-gray-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <input
                    value={type.description}
                    onChange={(e) => updateType(setDraft, index, { description: e.target.value })}
                    placeholder="Short description (optional)"
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Open hours</p>
            <div className="space-y-2">
              {MEETING_DAYS.map((day) => {
                const window = draft.hours[day.key][0]
                const open = !!window
                return (
                  <div key={day.key} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDraft({
                        ...draft,
                        hours: {
                          ...draft.hours,
                          [day.key]: open ? [] : [{ start: '09:00', end: '17:00' }],
                        },
                      })}
                      className={`w-10 h-6 rounded-full text-[10px] font-semibold ${open ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}
                    >
                      {day.key.toUpperCase()}
                    </button>
                    <span className="w-20 text-xs text-gray-600">{day.label.slice(0, 3)}</span>
                    {open ? (
                      <>
                        <input type="time" value={window.start} onChange={(e) => setWindow(setDraft, day.key, { start: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="time" value={window.end} onChange={(e) => setWindow(setDraft, day.key, { end: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">Closed</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Notice (hours)">
              <input type="number" min={0} max={168} value={draft.min_notice_hours} onChange={(e) => setDraft({ ...draft, min_notice_hours: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Book ahead (days)">
              <input type="number" min={1} max={90} value={draft.window_days} onChange={(e) => setDraft({ ...draft, window_days: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Buffer (minutes)">
              <input type="number" min={0} max={240} value={draft.buffer_minutes} onChange={(e) => setDraft({ ...draft, buffer_minutes: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Start times every">
              <select value={draft.start_interval_minutes} onChange={(e) => setDraft({ ...draft, start_interval_minutes: Number(e.target.value) })} className={inputClass}>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>60 min</option>
              </select>
            </Field>
          </div>

          <label className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-sm text-gray-700">Look busier</span>
              <span className="block text-xs text-gray-500">Hides some open times so a quiet week does not look wide open. Times that are actually booked stay blocked.</span>
            </span>
            <Toggle on={draft.look_busier} onClick={() => setDraft({ ...draft, look_busier: !draft.look_busier })} />
          </label>

          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Save meeting screen
          </button>
        </div>
      ) : (
        <div className="p-4 sm:p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : screens.length === 0 ? (
            <p className="text-sm text-gray-500">No meeting screens yet. Create one and share the link.</p>
          ) : (
            screens.map((screen) => (
              <div key={screen.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <button type="button" onClick={() => startEdit(screen)} className="text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{screen.name}</p>
                    <p className="text-xs text-gray-500 truncate">{screenPath(businessSlug, screen.slug)}</p>
                  </button>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${screen.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {screen.enabled ? 'Live' : 'Off'}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => void copyLink(screen.slug)} className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg">
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                  </button>
                  <button type="button" onClick={() => startEdit(screen)} className="px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg">
                    Edit
                  </button>
                  <button type="button" onClick={() => void remove(screen)} className="px-2.5 py-1.5 text-xs font-medium text-red-600">
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
          <button
            type="button"
            onClick={startNew}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <Plus className="h-4 w-4 mr-2" /> New meeting screen
          </button>
        </div>
      )}
    </div>
  )
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

function isHex(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(value)
}

function httpUrl(value: string) {
  let raw = value.trim()
  if (!raw) return ''
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) raw = `https://${raw}`
  try {
    const url = new URL(raw)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''
    if (!url.hostname.includes('.')) return ''
    return url.toString()
  } catch {
    return ''
  }
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={isHex(value) ? value : '#2563eb'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 rounded-lg border border-gray-200 bg-white"
        />
        <input value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
      </div>
    </Field>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  )
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`relative inline-flex h-6 w-11 items-center rounded-full ${on ? 'bg-blue-600' : 'bg-gray-300'}`} aria-pressed={on}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

function mapScreen(row: Record<string, unknown>): MeetingScreen {
  return {
    id: String(row.id),
    business_id: String(row.business_id),
    calendar_sync_id: row.calendar_sync_id ? String(row.calendar_sync_id) : null,
    name: String(row.name || ''),
    slug: String(row.slug || ''),
    enabled: row.enabled === true,
    headline: String(row.headline || ''),
    intro: String(row.intro || ''),
    host_name: String(row.host_name || ''),
    show_logo: row.show_logo === true,
    accent_color: isHex(String(row.accent_color || '')) ? String(row.accent_color) : '#2563eb',
    background_color: isHex(String(row.background_color || '')) ? String(row.background_color) : '#eef2ff',
    return_label: String(row.return_label || '').slice(0, 80),
    return_url: String(row.return_url || ''),
    timezone: String(row.timezone || 'America/Vancouver'),
    hours: normalizeHours(row.hours),
    meeting_types: normalizeTypes(row.meeting_types),
    buffer_minutes: Number(row.buffer_minutes) || 0,
    min_notice_hours: Number(row.min_notice_hours) || 0,
    window_days: Number(row.window_days) || 30,
    start_interval_minutes: Number(row.start_interval_minutes) || 30,
    look_busier: row.look_busier === true,
  }
}

function clamp(n: number, min: number, max: number) {
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, Math.round(n)))
}

function updateType(
  setDraft: React.Dispatch<React.SetStateAction<Draft | null>>,
  index: number,
  patch: Partial<MeetingTypeDraft>,
) {
  setDraft((prev) => prev && ({
    ...prev,
    meeting_types: prev.meeting_types.map((type, i) => i === index ? { ...type, ...patch } : type),
  }))
}

function setWindow(
  setDraft: React.Dispatch<React.SetStateAction<Draft | null>>,
  day: keyof WeekHours,
  patch: Partial<{ start: string; end: string }>,
) {
  setDraft((prev) => {
    if (!prev) return prev
    const current = prev.hours[day][0] || { start: '09:00', end: '17:00' }
    return { ...prev, hours: { ...prev.hours, [day]: [{ ...current, ...patch }] } }
  })
}
